import React, { useEffect, useState } from 'react';
import { TrashIcon, PlusIcon } from '@heroicons/react/20/solid';
import {
  BanknotesIcon,
  ReceiptPercentIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { Select } from 'antd';
import { TWButton, TWModal, TWDataTable } from '../../../../components/ui';
import { headerCls } from '../../../../data/columns';
import moment from 'moment';
import ModalDelete from '../../../../components/modal-delete/modal-delete';
import { SkeletonBlock } from '../../../../components/skeleton/skeleton';
import subscriptionApi from '../../../../services/subscriptionApi';
import financeApi from '../../../../services/financeApi';
import { formatCurrency } from '../../../../utils/baseUtils';
import { useParams, Link } from 'react-router-dom';

const FieldLabel = ({ children }) => (
  <label className="block text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
    {children}
  </label>
);

const months = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 },
];

const typeGradientFor = (name) => {
  switch (name) {
    case 'Payment':
      return 'from-emerald-500 to-teal-600';
    case 'Subscription Fee':
      return 'from-blue-500 to-indigo-600';
    case 'Invoice':
      return 'from-amber-500 to-orange-600';
    case 'Refund':
      return 'from-rose-500 to-red-600';
    default:
      return 'from-slate-500 to-slate-600';
  }
};

// `onBack` (spec 2026-10-20, admin company page: Account Activity shows a
// Stripe company's payouts, §1.3): given only when this ledger was reached
// through the Payouts view's "View payout history" — it draws "← Back to
// payouts" at the top of the header. A legacy company never gets it: there
// is nothing to go back to. Nothing else about the ledger changes.
const AccountActivity = ({ companyDetail, setNotf, onBack }) => {
  const { id } = useParams();
  const [filter, setFilter] = useState({
    storeid: null,
    dateRange: null,
    month: null,
  });
  const [financeList, setFinanceList] = useState([]);
  const [financeTypeList, setFinanceTypeList] = useState([]);
  const [balance, setBalance] = useState(0);
  const [deletedSubscription, setDeletedSubscription] = useState({});
  const [loading, setLoading] = useState({
    listLoading: true,
    upsertLoading: false,
    deleteLoading: false,
  });
  const [form, setForm] = useState({
    companyid: id,
    financetypeid: '',
    storeid: '',
    description: '',
    selectedMonth: '',
    selectedYear: '',
    selectedWeek: '',
    code: '',
    amount: 0,
  });
  const [dialogs, setDialogs] = useState({ upsert: false, delete: false });
  const [errors, setErrors] = useState({
    financetypeid: false,
    amount: false,
  });
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: null,
  });

  const getFinanceList = async () => {
    try {
      const query = `?companyid=${id}&pageNumber=${pagination.pageNumber}&pageSize=${pagination.pageSize}`;
      const list = await financeApi.list(query);
      if (list.data.data.length > 0) {
        setFinanceList(list.data.data);
        setPagination({
          pageNumber: list.data.pageNumber,
          pageSize: list.data.pageSize,
          totalRecords: list.data.totalRecords,
        });
      } else {
        setFinanceList([]);
      }
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const onFormChange = (val, key) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const onClickAdd = () => {
    setForm({
      companyid: id,
      financetypeid:
        financeTypeList.find((x) => x.name === 'Payment')?.id || '',
      description: '',
      code: '',
      selectedYear: moment().year(),
      selectedMonth: moment().month() + 1,
      selectedWeek: moment().isoWeek(),
      amount: 0,
    });
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onFormSubmit = async () => {
    if (!form.financetypeid || !form.amount) {
      setErrors((prev) => ({
        ...prev,
        financetypeid: !form.financetypeid,
        amount: !form.amount,
      }));
      return;
    }
    setErrors({ financetypeid: false, amount: false });

    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = { ...form };
      body.code = createCode(body.financetypeid);
      await financeApi.upsert(body);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      await getFinanceList();
      await getBalance();
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      setDialogs((prev) => ({ ...prev, upsert: false }));
    }
  };

  const onCloseUpsert = () => {
    setErrors({ financetypeid: false, amount: false });
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  const onClickDelete = (sub) => {
    setDeletedSubscription(sub);
    setDialogs((prev) => ({ ...prev, delete: true }));
  };

  const onConfirmDelete = async () => {
    setLoading((prev) => ({ ...prev, deleteLoading: true }));
    try {
      await subscriptionApi.deleteById(deletedSubscription.id);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setDialogs((prev) => ({ ...prev, delete: false, upsert: false }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
    }
  };

  const getFinanceTypeList = async () => {
    try {
      setLoading((prev) => ({ ...prev, listLoading: true }));
      // Every type, not the server's first page of ten — this list is a picker.
      const list = await financeApi.getTypeList('?pageSize=1000');
      setFinanceTypeList(list.data.data);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    }
  };

  const getBalance = async () => {
    try {
      const list = await financeApi.getBalanceById(id);
      setBalance(list.data.data);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    }
  };

  const getWeekOptions = () => {
    const options = [];
    for (let i = 1; i <= 52; i++) {
      options.push({ label: `Week ${i}`, value: i });
    }
    return options;
  };

  const getBackAndNextYear = () => {
    const options = [];
    for (let i = -1; i <= 1; i++) {
      options.push({
        label: `${moment().year() + i}`,
        value: moment().year() + i,
      });
    }
    return options;
  };

  const createCode = (financetypeid) => {
    if (
      financetypeid ===
      financeTypeList.find((x) => x.name === 'Subscription Fee')?.id
    ) {
      return `${form.selectedYear}:${form.selectedMonth}`;
    } else if (
      financetypeid === financeTypeList.find((x) => x.name === 'Invoice')?.id
    ) {
      return `${form.selectedYear}:${form.selectedWeek}`;
    }
    return '';
  };

  useEffect(() => {
    getFinanceTypeList();
    getBalance();
    getFinanceList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageNumber]);

  const subscriptionId = financeTypeList.find(
    (x) => x.name === 'Subscription Fee',
  )?.id;
  const invoiceId = financeTypeList.find((x) => x.name === 'Invoice')?.id;
  const showYear =
    form.financetypeid === invoiceId || form.financetypeid === subscriptionId;
  const showMonth = form.financetypeid === subscriptionId;
  const showWeek = form.financetypeid === invoiceId;

  return (
    <section className="space-y-5">
      {/* Summary strip */}
      <div className="rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div className="flex flex-col gap-4 border-b border-slate-200 bg-slate-50/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/40 lg:flex-row lg:items-center lg:justify-between">
          <div>
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="mb-3 -mx-1 inline-flex items-center gap-1 rounded px-1 text-xs font-medium text-blue-600 transition hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-sky-400 dark:hover:text-sky-300 dark:focus-visible:ring-sky-400"
              >
                ← Back to payouts
              </button>
            ) : null}
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-sm">
                <BanknotesIcon className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  Account Activity
                </h2>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  Finance entries for {companyDetail?.name || 'this company'}.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex items-center justify-between gap-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 dark:border-emerald-500/20 dark:bg-emerald-500/10">
              <span className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-emerald-700 dark:text-emerald-300">
                Balance
              </span>
              <span className="text-lg font-bold text-emerald-900 dark:text-emerald-200">
                {formatCurrency(balance)}
              </span>
            </div>

            <Select
              allowClear
              className="company-detail-select min-w-[140px]"
              placeholder="Month"
              value={filter.month || undefined}
              onChange={(e) => setFilter((prev) => ({ ...prev, month: e }))}
              options={months}
            />

            <button
              type="button"
              onClick={onClickAdd}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md border-none bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 dark:bg-sky-500 dark:hover:bg-sky-600"
            >
              <PlusIcon className="h-4 w-4" />
              New Entry
            </button>
          </div>
        </div>

        {/* Table / list */}
        <div className="px-2 py-2 sm:px-4">
          {loading.listLoading ? (
            <div className="reveal" aria-hidden="true">
              <table className="min-w-full divide-y divide-gray-300 dark:divide-slate-500">
                <thead>
                  <tr>
                    <th className="py-3.5 pr-3 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Type
                    </th>
                    <th className="py-3.5 px-4 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Explanation
                    </th>
                    <th className="py-3.5 px-4 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Code
                    </th>
                    <th className="py-3.5 px-4 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Created
                    </th>
                    <th className="py-3.5 px-4 text-right text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-500">
                  {[...Array(6)].map((_, i) => (
                    <tr key={i}>
                      <td className="whitespace-nowrap py-4 pr-3">
                        <div className="flex items-center gap-3">
                          <SkeletonBlock className="h-8 w-8 !rounded-md" />
                          <SkeletonBlock className="h-4 w-32" />
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4">
                        <SkeletonBlock className="h-4 w-48" />
                      </td>
                      <td className="whitespace-nowrap px-4 py-4">
                        <SkeletonBlock className="h-5 w-16 !rounded-md" />
                      </td>
                      <td className="whitespace-nowrap px-4 py-4">
                        <SkeletonBlock className="h-4 w-24" />
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-right">
                        <SkeletonBlock className="ml-auto h-4 w-20" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : financeList.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-slate-300 px-6 py-16 text-center dark:border-slate-600">
              <div className="flex h-14 w-14 items-center justify-center rounded-md bg-slate-100 text-slate-400 dark:bg-slate-700/60 dark:text-slate-500">
                <ReceiptPercentIcon className="h-7 w-7" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  No finance entries yet.
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Add a new entry to record a payment, invoice, or subscription
                  fee.
                </p>
              </div>
            </div>
          ) : (
            <TWDataTable
              columns={[
                {
                  title: <span className={headerCls}>Type</span>,
                  key: 'type',
                  render: (_, finance) => {
                    const typeName = finance.financetype?.name || '—';
                    const gradient = typeGradientFor(typeName);
                    return (
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br ${gradient} text-white shadow-sm`}
                        >
                          <BanknotesIcon className="h-4 w-4" />
                        </div>
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          {typeName}
                        </span>
                      </div>
                    );
                  },
                },
                {
                  title: <span className={headerCls}>Explanation</span>,
                  key: 'description',
                  render: (_, finance) =>
                    finance.details?.length > 0 ? (
                      <Link
                        to={`/dashboard/companies/${id}/orders?financeid=${finance.id}`}
                        className="text-sm font-medium text-blue-600 hover:underline dark:text-sky-400"
                      >
                        {finance.description || '—'}
                      </Link>
                    ) : (
                      <span className="text-sm text-slate-600 dark:text-slate-300">
                        {finance.description || '—'}
                      </span>
                    ),
                },
                {
                  title: <span className={headerCls}>Code</span>,
                  key: 'code',
                  render: (_, finance) =>
                    finance.code ? (
                      <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700 dark:bg-slate-700/60 dark:text-slate-200">
                        {finance.code}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    ),
                },
                {
                  title: <span className={headerCls}>Created</span>,
                  key: 'createdate',
                  render: (_, finance) => (
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      {moment
                        .utc(finance.createdate)
                        .local()
                        .format('MM/DD/YYYY')}
                    </span>
                  ),
                },
                {
                  title: (
                    <span className={`${headerCls} block text-right`}>
                      Amount
                    </span>
                  ),
                  key: 'amount',
                  align: 'right',
                  render: (_, finance) => (
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {Number(finance.amount || 0).toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      })}
                    </span>
                  ),
                },
              ]}
              data={financeList}
              rowKey="id"
              pagination={
                pagination.totalRecords > pagination.pageSize
                  ? {
                      current: pagination.pageNumber,
                      pageSize: pagination.pageSize,
                      total: pagination.totalRecords,
                      showTotal: (total, range) =>
                        `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                      onChange: (page) =>
                        setPagination((prev) => ({
                          ...prev,
                          pageNumber: page,
                        })),
                      showSizeChanger: false,
                    }
                  : undefined
              }
            />
          )}
        </div>
      </div>

      {/* Upsert Modal */}
      <TWModal
        open={dialogs.upsert}
        onClose={onCloseUpsert}
        title={form.id ? 'Edit Finance Entry' : 'New Finance Entry'}
        description="Record a payment, invoice, or subscription fee."
        size="2xl"
        showCloseButton
        dismissible={false}
        footer={
          <div className="flex items-center justify-between gap-2">
            {form.id ? (
              <TWButton
                variant="danger"
                icon={TrashIcon}
                label="Delete"
                disabled={loading.deleteLoading}
                onClick={() => onClickDelete(form)}
              />
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <TWButton
                variant="default"
                label="Cancel"
                disabled={loading.upsertLoading}
                onClick={onCloseUpsert}
              />
              <TWButton
                variant="primary"
                label={
                  loading.upsertLoading
                    ? 'Saving…'
                    : form.id
                      ? 'Save Changes'
                      : 'Add Entry'
                }
                disabled={loading.upsertLoading}
                loading={loading.upsertLoading}
                onClick={onFormSubmit}
              />
            </div>
          </div>
        }
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <FieldLabel>Finance Type *</FieldLabel>
              <Select
                allowClear
                size="large"
                className="company-detail-select mt-2 w-full"
                placeholder="Select type"
                value={form.financetypeid || undefined}
                onChange={(val) => onFormChange(val, 'financetypeid')}
                options={financeTypeList
                  .filter((t) => ![20, 30].includes(t.code))
                  .map(({ name, id }) => ({ label: name, value: id }))}
              />
              {errors.financetypeid && (
                <p className="mt-1 text-xs text-rose-600">
                  Finance Type cannot be empty
                </p>
              )}
            </div>

            <div>
              <FieldLabel>
                <span className="inline-flex items-center gap-1">
                  <CurrencyDollarIcon className="h-3 w-3" />
                  Amount *
                </span>
              </FieldLabel>
              <div className="relative mt-2">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-slate-400">
                  $
                </span>
                <input
                  value={form.amount}
                  onChange={(e) => onFormChange(e.target.value, 'amount')}
                  placeholder="0.00"
                  type="number"
                  className="block w-full rounded-md border border-slate-200 bg-white py-2 pl-7 pr-3 text-sm text-slate-900 placeholder-slate-400 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400/20"
                />
              </div>
              {errors.amount && (
                <p className="mt-1 text-xs text-rose-600">
                  Amount cannot be empty
                </p>
              )}
            </div>
          </div>

          {(showYear || showMonth || showWeek) && (
            <div className="grid grid-cols-1 gap-4 rounded-md border border-slate-200 bg-slate-50/60 p-4 dark:border-slate-700 dark:bg-slate-900/40 sm:grid-cols-3">
              {showYear && (
                <div>
                  <FieldLabel>
                    <span className="inline-flex items-center gap-1">
                      <CalendarDaysIcon className="h-3 w-3" />
                      Year *
                    </span>
                  </FieldLabel>
                  <Select
                    allowClear
                    defaultValue={moment().year()}
                    size="large"
                    className="company-detail-select mt-2 w-full"
                    placeholder="Year"
                    onChange={(val) => onFormChange(val, 'selectedYear')}
                    options={getBackAndNextYear()}
                  />
                </div>
              )}
              {showMonth && (
                <div>
                  <FieldLabel>Month *</FieldLabel>
                  <Select
                    allowClear
                    size="large"
                    defaultValue={moment().month() + 1}
                    className="company-detail-select mt-2 w-full"
                    placeholder="Month"
                    onChange={(val) => onFormChange(val, 'selectedMonth')}
                    options={months}
                  />
                </div>
              )}
              {showWeek && (
                <div>
                  <FieldLabel>Week *</FieldLabel>
                  <Select
                    allowClear
                    defaultValue={moment().isoWeek()}
                    size="large"
                    className="company-detail-select mt-2 w-full"
                    placeholder="Week"
                    onChange={(val) => onFormChange(val, 'selectedWeek')}
                    options={getWeekOptions()}
                  />
                </div>
              )}
            </div>
          )}

          <div>
            <FieldLabel>
              <span className="inline-flex items-center gap-1">
                <DocumentTextIcon className="h-3 w-3" />
                Description
              </span>
            </FieldLabel>
            <textarea
              onChange={(e) => onFormChange(e.target.value, 'description')}
              value={form.description}
              rows={3}
              placeholder="e.g. Subscription payment for May 2023"
              className="mt-2 block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400/20"
            />
          </div>
        </div>
      </TWModal>

      <ModalDelete
        title="Delete Subscription"
        loading={loading.deleteLoading}
        open={dialogs.delete}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, delete: false }))
        }
        onOk={onConfirmDelete}
        item={deletedSubscription}
      />
    </section>
  );
};

export default AccountActivity;
